<template>
    <section>
        <div v-if="emptyHistory" class="tx-history-empty-panel">
            {{ $t('address.tx_table.empty') }}

            <button class="user-nft__btn" v-if="filtersApplied" v-on:click="resetFilters">
                {{ $t('address.tx_table.reset_filters') }}
            </button>
        </div>

        <div v-show="!emptyHistory" class="tx-history-wrap desktop-table">
            <table class="tx-table">
                <thead v-if="!isMobile">
                    <tr>
                        <th width="100">
                            <div class="tx-table__cell" v-text="$t('address.tx_table.age')" />
                        </th>
                        <th width="100" style="padding-left: 22px">
                            <div class="tx-table__cell" v-text="$t('address.tx_table.event')" />
                        </th>
                        <th>
                            <div class="tx-table__cell" v-text="$t('address.tx_table.address')" />
                        </th>
                        <th v-if="showTxComments">
                            <div class="tx-table__cell tx-table__cell--comment" v-text="$t('address.tx_table.comment')" />
                        </th>
                        <th class="column-wide">
                            <div class="tx-table__cell tx-table__cell--align-right" style="padding-right: 26px;"
                                v-text="$t('address.tx_table.value')" />
                        </th>
                        <!-- The icons after the value hang over this column; min-width keeps it next to the stretched comment column: -->
                        <th v-pre width="14" style="min-width: 14px"></th>
                    </tr>
                </thead>

                <template v-if="address">
                    <template v-for="tx in pendingTransactions">
                        <component
                            v-for="(msg, idx) in tx.messages"
                            v-bind:key="`pending_tx_${tx.hash}_msg_${idx}`"
                            v-bind:is="isMobile ? 'tx-row-mobile' : 'tx-row-simple'"
                            v-bind:class="{ 'sub-list': idx > 0 }"
                            v-bind:address="address"
                            v-bind:txHash="tx.hash"
                            v-bind:txLt="tx.lt"
                            v-bind:timestamp="tx.timestamp"
                            v-bind:fee="tx.fee"
                            v-bind:exitCode="tx.exit_code"
                            v-bind:outputCount="tx.output_count"
                            v-bind:action="msg.action"
                            v-bind:event="msg.event"
                            v-bind:meta="msg.meta"
                            v-bind:hideIcon="hideIcon"
                            v-bind="msg"
                            v-bind:msg="msg"
                            v-bind:encrypted="msg.encrypted"
                            v-bind:finality="msg.finality"
                        />
                    </template>
                    <template v-for="tx in allTransactions">
                        <component
                            v-for="(msg, idx) in tx.messages"
                            v-bind:key="`tx_${tx.hash}_msg_${idx}`"
                            v-bind:is="isMobile ? 'tx-row-mobile' : 'tx-row-simple'"
                            v-bind:class="{ 'sub-list': idx > 0 }"
                            v-bind:address="address"
                            v-bind:txHash="tx.hash"
                            v-bind:txLt="tx.lt"
                            v-bind:timestamp="tx.timestamp"
                            v-bind:fee="tx.fee"
                            v-bind:exitCode="tx.exit_code"
                            v-bind:outputCount="tx.output_count"
                            v-bind:action="msg.action"
                            v-bind:event="msg.event"
                            v-bind:meta="msg.meta"
                            v-bind:hideIcon="hideIcon"
                            v-bind="msg"
                            v-bind:msg="msg"
                            v-bind:encrypted="msg.encrypted"
                        />
                    </template>
                </template>

                <template v-if="!address || allTransactions.length === 0">
                    <component
                        v-bind:is="isMobile
                            ? 'tx-row-skeleton-mobile'
                            : 'tx-row-skeleton-simple'"
                        v-for="i in 8"
                        v-bind:key="`tx_skeleton_${i}`"
                        v-bind:hide-icon="hideIcon"/>
                </template>
            </table>
        </div>

        <ui-mugen-scroll
            v-bind:handler="loadMore"
            v-bind:shouldHandle="shouldHandleScroll"
            v-bind:showButton="showPreloader"
            v-bind:isLoading="isLoading"/>
    </section>
</template>

<script>
import { mapState } from 'vuex';
import { getPendingActions, getToncenterActions } from '~/api/toncenterV3';
import debounce from 'lodash.debounce';
import { closeWebsocketConnection, startTxAndStateWebsocket, stopTxAndStateWebsockets } from '~/api/websocket';
import TxRowSkeletonSimple from './TxRowSkeletonSimple.vue';
import TxRowSkeletonMobile from './TxRowSkeletonMobile.vue';
import TxRowSimple from './TxRowSimple.vue';
import TxRowMobile from './TxRowMobile.vue';

// The largest page /actions gives:
const ACTIONS_MAX_LIMIT = 1000;

export default {
    pendingInterval: undefined,
    props: {
        address: {
            type: String,
            required: false,
        },
        shouldLoadPendings: {
            type: Boolean,
        },
    },

    data() {
        return {
            transactions: [],
            isLoading: true,
            hasMore: true,
            nextPage: null,
            hideIcon: false,
            isUnmounted: false,
        };
    },

    computed: {
        allTransactions() {
            return [...this.newTransactions, ...this.transactions];
        },
        emptyHistory() {
            return !this.isLoading && !this.allTransactions.length && !this.pendingTransactions.length;
        },
        shouldHandleScroll() {
            return !this.isLoading && this.address && this.hasMore && this.transactions.length > 0;
        },

        showPreloader() {
            return this.address && this.transactions.length > 0 && this.hasMore;
        },

        initiatedByAccount() {
            return this.$store.getters.initiatedByAccountFor(this.address);
        },

        filtersApplied() {
            return this.$store.getters.historyFiltersApplied(this.address);
        },

        ...mapState({
            dataSource: 'txTableSource',
            actionTypeFilter: 'actionTypeFilter',
            excludeGroups: 'excludeGroups',
            datePickerFilter: 'datePickerFilter',
            transactionDirection: 'transactionDirection',
            newTransactions: 'websocketTransactions',
            pendingTransactions: 'websocketPendingTransactions',
            showTxComments: 'showTxComments',
        }),
    },

    watch: {
        address: {
            immediate: false,
            handler: 'resetData',
        },

        // dataSource: {
        //     immediate: false,
        //     handler() {
        //         this.removeInterval();
        //         this.resetData();
        //         this.startInterval();
        //     },
        // },

        actionTypeFilter: {
            immediate: false,
            handler() {
                this.resetData();
            },
        },

        datePickerFilter: {
            immediate: false,
            handler() {
                this.resetData();
            },
        },

        transactionDirection: {
            immediate: false,
            handler() {
                this.resetData();
            },
        },

        initiatedByAccount: {
            immediate: false,
            handler() {
                this.resetData();
            },
        },

        newTransactions: {
            immediate: false,
            handler() {
                this.isLoading = false;
            },
        },

        excludeGroups: {
            immediate: false,
            handler() {
                this.debouncedFilters();
            },
        },
    },

    created() {
        // Обернем метод в debounce и сохраним как отдельную функцию компонента
        this.debouncedFilters = debounce(() => {
            this.resetData();
        }, 1200);
    },

    mounted() {
        this.resetData();
    },

    methods: {
        resetData() {
            if (!this.address) return;

            this.transactions = [];
            this.isLoading = true;
            this.hasMore = true;
            this.nextPage = null;
            this.isUnmounted = false;
            stopTxAndStateWebsockets();

            this.$nextTick(this.loadData);
        },

        /**
         * A page that is all one unfinished trace has nothing to show yet (its actions are held
         * back until the trace ends), so the next one is asked for right away, twice as large.
         */
        async loadPage(limit, page) {
            const transactions = await getToncenterActions(this.address, { limit, ...page }, this.actionTypeFilter);

            if (!transactions.length && transactions.nextPage) {
                return this.loadPage(Math.min(limit * 2, ACTIONS_MAX_LIMIT), transactions.nextPage);
            }

            return transactions;
        },

        async loadData() {
            this.isLoading = true;
            const limit = 50;

            if (!this.address) return;

            try {
                const transactions = await this.loadPage(limit);

                this.transactions = transactions;
                this.nextPage = transactions.nextPage;
                this.hasMore = transactions.nextPage !== null;
            } catch (error) {
                console.error('Error loading transactions:', error);
            } finally {
                this.isLoading = false;
            }

            // A date range holds past actions only: no pending rows and no live feed on top of it.
            if (this.datePickerFilter[0]) {
                this.$store.state.websocketPendingTransactions = [];
                return;
            }

            try {
                const pendingTransactions = await getPendingActions(this.address);
                this.$store.state.websocketPendingTransactions = pendingTransactions;
            } catch (error) {
                console.error('Error loading pending transactions:', error);
            }

            startTxAndStateWebsocket(this.address);
        },

        async loadMore() {
            if (this.isLoading || !this.hasMore) return;

            this.isLoading = true;
            const limit = 50;
            const page = this.nextPage;

            try {
                const newTx = await this.loadPage(limit, page);

                // The list was loaded anew (another address, filter or dates) while this page was on its way:
                if (this.nextPage !== page) return;

                this.nextPage = newTx.nextPage;
                this.hasMore = newTx.nextPage !== null;
                this.transactions = [...this.transactions, ...newTx];
            } catch (error) {
                console.error('Error loading more transactions:', error);
            } finally {
                this.isLoading = false;
            }
        },

        resetFilters() {
            this.$store.dispatch('resetHistoryFilters', this.address);
        },
    },

    beforeDestroy() {
        closeWebsocketConnection();
        if (this.debouncedFilters && this.debouncedFilters.cancel) {
            this.debouncedFilters.cancel();
        }
    },

    components: {
        TxRowSimple, TxRowMobile, TxRowSkeletonMobile, TxRowSkeletonSimple,
    },
};
</script>
